import isString from 'lodash/isString';
import React, { useState, useEffect, useContext, useRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { FormControl, FormGroup } from 'react-bootstrap';
import { FormContext, skipUpdate } from '../FormContext';
import FormInputLabel from './parts/FormInputLabel';
import FormInputHelp from './parts/FormInputHelp';
import '../styles/select.scss';

const FormInputSelectItem = React.memo(props => {
  const { controlId, field, hidden, disabled, noLabel, ignoreCase, touched, originalValue } = props;

  const firstUpdate = useRef(true);

  const [dirty, setDirty] = useState(props.value !== originalValue);
  const [errorMessage, setErrorMessage] = useState(props.errorMessage);
  const [options, setOptions] = useState(props.options);
  const [valid, setValid] = useState(props.valid);
  const [value, setValue] = useState(props.value);

  const [info, setInfo] = useState(props.info);
  const [warning, setWarning] = useState(props.warning);

  const { ee, trackDirty } = useContext(FormContext);

  const consume = ({ action, payload: data }) => {
    if (action !== 'update') {
      return;
    }

    if (data.warning !== undefined) {
      if (data.warning !== warning) {
        props.setWarning(field.name, data.warning);
        setWarning(data.warning);
      }
    }

    if (data.info !== undefined) {
      if (data.info !== info) {
        props.setInfo(field.name, data.info);
        setInfo(data.info);
      }
    }

    if (data.value !== undefined) {
      setValue(data.value);
    }

    if (data.options !== undefined) {
      setOptions(data.options);
    }
  };

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    let dirty = trackDirty;

    if (originalValue === value) {
      dirty = false;
    }

    setDirty(dirty);
    props.onChange(field.name, value, () => {
      const result = props.onBlur(field.name);

      if (result) {
        setValue(result.displayValue !== undefined ? result.displayValue : value);
        setValid(result.valid !== undefined ? result.valid : valid);
        setErrorMessage(result.validError !== undefined ? result.validError : errorMessage);
        setDirty(originalValue !== value);
      }
    });
  }, [value]);

  // Original Value changed from props (usually a submit)
  useEffect(() => {
    if (originalValue === value) {
      setDirty(false);
    } else {
      setDirty(true);
    }
  }, [originalValue]);

  // Validation state changed from props (usually a submit)
  useEffect(() => {
    if (props.valid !== valid) {
      setValid(props.valid);
      setErrorMessage(props.errorMessage);
    }
  }, [props.valid]);

  // When the options change, check if the value is still valid
  useEffect(() => {
    if (!options.length) {
      setValue('');
      return;
    }

    // If value is not set, set value to use first option
    if ((value === null || value === undefined) && field.noEmptyOption) {
      setValue(options[0].value);
      return;
    }

    // get option values, if current state value isn't present, set to first option
    const optionValues = options.map(x => String(x.value));

    if (!optionValues.includes(String(value))) {
      if (field.noEmptyOption) {
        setValue(options[0].value);
      } else {
        setValue('');
      }
    }
  }, [options]);

  useEffect(() => {
    ee.on(controlId, consume);

    return () => {
      ee.off(controlId);
    };
  }, []);

  const renderOptions = () => {
    const { field } = props;
    const renderedOptions = [];

    if (!field.noEmptyOption) {
      renderedOptions.push(<option value="" key="empty" />);
    }

    options.forEach(option => {
      if (typeof option === 'object') {
        renderedOptions.push(
          <option value={option.value} key={option.value}>
            {option.label}
          </option>
        );
      } else {
        renderedOptions.push(
          <option value={option} key={option}>
            {option}
          </option>
        );
      }
    });

    return renderedOptions;
  };

  let currentValue = value;

  if (ignoreCase && currentValue && isString(currentValue)) {
    // check if value matches an option and if case differs, update value with correct case
    for (const option of options) {
      if (
        option.value &&
        isString(option.value) &&
        currentValue !== option.value &&
        currentValue.toLowerCase() === option.value.toLowerCase()
      ) {
        currentValue = option.value; // eslint-disable-line
        break;
      }
    }
  }

  const additionalProps = {};

  const isDirty = dirty && valid && !field.readOnly && trackDirty;
  const isReadOnly = field.readOnly || disabled || !options.length;
  const isInvalid = !valid && touched;

  const containerClass = classNames('form-select', {
    'is-invalid': isInvalid,
    'is-dirty': isDirty,
    'is-readonly': isReadOnly,
    'is-hidden': hidden,
  });

  return (
    <FormGroup
      controlId={controlId}
      className={containerClass}
      key={controlId}
      id={`${controlId}-control`}
      {...additionalProps}
    >
      <FormInputLabel hide={noLabel}>{field.label}</FormInputLabel>
      <FormControl
        as="select"
        disabled={isReadOnly}
        max={field.max}
        maxLength={field.maxLen}
        min={field.min}
        minLength={field.minLen}
        onChange={e => setValue(e.target.value)}
        pattern={field.pattern}
        readOnly={field.readOnly}
        required={field.required}
        type={field.type ? field.type : 'text'}
        value={value}
      >
        {renderOptions()}
      </FormControl>
      {warning && <div className="warning-block">{warning}</div>}
      {info && <div className="info-block">{info}</div>}
      <FormInputHelp errorText={errorMessage}>{field.helpText}</FormInputHelp>
    </FormGroup>
  );
}, skipUpdate);

FormInputSelectItem.defaultProps = {
  controlId: '',
  disabled: false,
  errorMessage: '',
  field: {},
  hidden: false,
  ignoreCase: true,
  info: '',
  noLabel: false,
  onBlur: () => {},
  onChange: () => {},
  options: [],
  originalValue: '',
  setInfo: () => {},
  setWarning: () => {},
  touched: false,
  valid: true,
  value: '',
  warning: '',
};

FormInputSelectItem.propTypes = {
  controlId: PropTypes.string,
  disabled: PropTypes.bool,
  errorMessage: PropTypes.string,
  field: PropTypes.object,
  hidden: PropTypes.bool,
  ignoreCase: PropTypes.bool,
  info: PropTypes.string,
  noLabel: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  options: PropTypes.array,
  originalValue: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  setInfo: PropTypes.func,
  setWarning: PropTypes.func,
  touched: PropTypes.bool,
  valid: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  warning: PropTypes.string,
};

export default FormInputSelectItem;
